import React, { useState, useEffect } from 'react';
import {
  Snackbar,
  Alert,
  AlertTitle,
  Slide,
  type SlideProps,
  IconButton,
  Box,
  Typography,
  Collapse,
} from '@mui/material';
import {
  Close as CloseIcon,
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon,
} from '@mui/icons-material';
import { type AppError, ErrorSeverity } from '../../types/error';
import { errorService } from '../../services/errorService';

interface ErrorNotificationProps {
  maxVisible?: number;
  autoHideDuration?: number;
  showDetails?: boolean;
}

interface ErrorNotificationItem {
  error: AppError;
  open: boolean;
  showDetails: boolean;
  isSuccess?: boolean;
}

// Custom slide transition
function SlideTransition(props: SlideProps) {
  return <Slide {...props} direction="up" />;
}

export const ErrorNotification: React.FC<ErrorNotificationProps> = ({
  maxVisible = 3,
  autoHideDuration = 6000,
  showDetails = false,
}) => {
  const [errorNotifications, setErrorNotifications] = useState<ErrorNotificationItem[]>([]);

  useEffect(() => {
    // Subscribe to error events
    const unsubscribe = errorService.onError((error: AppError) => {
      // Only show errors that should be visible to users
      if (error.severity === ErrorSeverity.LOW) return;

      const notificationItem: ErrorNotificationItem = {
        error,
        open: true,
        showDetails: true,
      };

      setErrorNotifications(prev => {
        const newNotifications = [notificationItem, ...prev].slice(0, maxVisible);
        return newNotifications;
      });
    });

    // Subscribe to success events
    const unsubscribeSuccess = errorService.onSuccess((message: string) => {
      const successItem: ErrorNotificationItem = {
        error: {
          id: `success_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          message,
          type: 'unknown' as any,
          severity: ErrorSeverity.LOW,
          timestamp: new Date(),
          userMessage: message,
        },
        open: true,
        showDetails: false,
        isSuccess: true,
      };

      setErrorNotifications(prev => {
        const newNotifications = [successItem, ...prev].slice(0, maxVisible);
        return newNotifications;
      });
    });

    return () => {
      unsubscribe();
      unsubscribeSuccess();
    };
  }, [maxVisible]);

  const handleClose = (errorId: string, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }

    setErrorNotifications(prev =>
      prev.map(item =>
        item.error.id === errorId ? { ...item, open: false } : item
      )
    );

    // Remove the notification after transition
    setTimeout(() => {
      setErrorNotifications(prev =>
        prev.filter(item => item.error.id !== errorId)
      );
    }, 300);
  };

  const toggleDetails = (errorId: string) => {
    setErrorNotifications(prev =>
      prev.map(item =>
        item.error.id === errorId
          ? { ...item, showDetails: !item.showDetails }
          : item
      )
    );
  };

  const getSeverityLevel = (item: ErrorNotificationItem): 'error' | 'warning' | 'info' | 'success' => {
    if (item.isSuccess) return 'success';
    switch (item.error.severity) {
      case ErrorSeverity.CRITICAL:
      case ErrorSeverity.HIGH:
        return 'error';
      case ErrorSeverity.MEDIUM:
        return 'warning';
      case ErrorSeverity.LOW:
      default:
        return 'info';
    }
  };

  const getAutoHideDuration = (item: ErrorNotificationItem): number | null => {
    if (item.isSuccess) return autoHideDuration;
    switch (item.error.severity) {
      case ErrorSeverity.CRITICAL:
        return null; // Don't auto-hide critical errors
      case ErrorSeverity.HIGH:
        return autoHideDuration * 2;
      case ErrorSeverity.MEDIUM:
        return autoHideDuration;
      case ErrorSeverity.LOW:
      default:
        return autoHideDuration / 2;
    }
  };

  const getTitle = (item: ErrorNotificationItem): string => {
    if (item.isSuccess) return 'Éxito';
    const type = item.error.type;
    if (type === 'unknown') return 'Error';
    return `${type.replace('_', ' ').toUpperCase()} Error`;
  };

  return (
    <>
      {errorNotifications.map((item, index) => (
        <Snackbar
          key={item.error.id}
          open={item.open}
          autoHideDuration={getAutoHideDuration(item)}
          onClose={(_, reason) => handleClose(item.error.id, reason)}
          TransitionComponent={SlideTransition}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          sx={{
            position: 'fixed',
            bottom: 16 + index * 80,
            zIndex: 1400 + index,
          }}
        >
          <Alert
            severity={getSeverityLevel(item)}
            variant="filled"
            sx={{ minWidth: 300, maxWidth: 600 }}
            action={
              <Box display="flex" alignItems="center">
                {!item.isSuccess && (
                  <IconButton
                    size="small"
                    color="inherit"
                    onClick={() => toggleDetails(item.error.id)}
                    aria-label={item.showDetails ? 'Hide details' : 'Show details'}
                  >
                    {item.showDetails ? <CollapseIcon /> : <ExpandIcon />}
                  </IconButton>
                )}
                <IconButton
                  size="small"
                  color="inherit"
                  onClick={() => handleClose(item.error.id)}
                  aria-label="Close"
                >
                  <CloseIcon />
                </IconButton>
              </Box>
            }
          >
            <AlertTitle>
              {getTitle(item)}
            </AlertTitle>
            <Typography variant="body2">
              {item.error.userMessage || item.error.message}
            </Typography>

            {!item.isSuccess && (
              <Collapse in={item.showDetails}>
                <Box mt={1} p={1} bgcolor="rgba(255,255,255,0.1)" borderRadius={1}>
                  <Typography variant="caption" component="pre" sx={{ fontSize: '0.7rem', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {item.error.code && <><strong>HTTP:</strong> {item.error.code}{'\n'}</>}
                    {item.error.context?.method && item.error.context?.url && (
                      <><strong>Request:</strong> {item.error.context.method} {item.error.context.url}{'\n'}</>
                    )}
                    {item.error.context?.serverMessage && (
                      <><strong>Server:</strong> {item.error.context.serverMessage}{'\n'}</>
                    )}
                    {item.error.details && <><strong>Details:</strong> {item.error.details}{'\n'}</>}
                    {item.error.context?.responseData && typeof item.error.context.responseData === 'object' && (
                      <><strong>Response:</strong> {JSON.stringify(item.error.context.responseData, null, 2)}{'\n'}</>
                    )}
                    <strong>Time:</strong> {item.error.timestamp.toLocaleTimeString()}
                  </Typography>
                </Box>
              </Collapse>
            )}
          </Alert>
        </Snackbar>
      ))}
    </>
  );
};

export default ErrorNotification;
